import React, { useState, useEffect } from "react";
import { MapPin, Zap, ShieldCheck, Wrench, Globe } from "lucide-react";

interface SplashScreenProps {
  isLoading: boolean;
  minDuration?: number; // Minimum display time in ms (default: 350ms for lightning speed)
  onFinish?: () => void;
}

export default function SplashScreen({
  isLoading,
  minDuration = 350,
  onFinish,
}: SplashScreenProps) {
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isRendered, setIsRendered] = useState(true);
  const [loadingStep, setLoadingStep] = useState(0);

  // Status message rotation for a lively, professional feel
  const loadingMessages = [
    "Connecting to Bhopal & Sehore Hub...",
    "Loading Live Appliance Inventory...",
    "Fetching ₹0 Advance Rental Plans...",
    "Welcome to RentLow.co.in!",
  ];

  useEffect(() => {
    // Check if crawler/bot to skip splash immediately
    const isBot =
      typeof navigator !== "undefined" &&
      /bot|google|baidu|bing|msn|duckduckbot|teoma|slurp|yandex|lighthouse|headless/i.test(
        navigator.userAgent
      );

    if (isBot) {
      setIsRendered(false);
      onFinish?.();
      return;
    }

    // Minimum display timer
    const minTimer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, minDuration);

    // Rotate status messages smoothly
    const step1 = setTimeout(() => setLoadingStep(1), 100);
    const step2 = setTimeout(() => setLoadingStep(2), 220);
    const step3 = setTimeout(() => setLoadingStep(3), 320);

    // Maximum failsafe timer: ensure splash never stays more than 600ms under any circumstance
    const maxFailsafe = setTimeout(() => {
      setMinTimeElapsed(true);
      setIsFadingOut(true);
      setTimeout(() => {
        setIsRendered(false);
        onFinish?.();
      }, 250);
    }, 600);

    return () => {
      clearTimeout(minTimer);
      clearTimeout(step1);
      clearTimeout(step2);
      clearTimeout(step3);
      clearTimeout(maxFailsafe);
    };
  }, [minDuration, onFinish]);

  // Trigger smooth fade out when both initial loading finished and minimum time elapsed
  useEffect(() => {
    if (!isLoading && minTimeElapsed) {
      setIsFadingOut(true);
      const exitTimer = setTimeout(() => {
        setIsRendered(false);
        onFinish?.();
      }, 300);
      return () => clearTimeout(exitTimer);
    }
  }, [isLoading, minTimeElapsed, onFinish]);

  if (!isRendered) return null;

  return (
    <div
      id="rentlow-splash-screen"
      role="status"
      aria-live="polite"
      aria-label="Loading RentLow.co.in - Appliance Rentals in Bhopal & Sehore"
      style={{ pointerEvents: isFadingOut ? 'none' : 'auto' }}
      onClick={() => {
        setIsFadingOut(true);
        setIsRendered(false);
        onFinish?.();
      }}
      onTouchStart={() => {
        setIsFadingOut(true);
        setIsRendered(false);
        onFinish?.();
      }}
      className={`fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-gradient-to-b from-slate-900 via-[#0B1528] to-slate-950 text-white select-none transition-all duration-300 ease-out cursor-pointer ${
        isFadingOut
          ? "opacity-0 pointer-events-none scale-102"
          : "opacity-100 pointer-events-auto scale-100"
      }`}
    >
      {/* Decorative ambient background glows */}
      <div className="absolute top-1/4 -left-20 w-80 h-80 bg-[#0077B6]/20 rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-[#759783]/20 rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,119,182,0.12)_0%,transparent_70%)] pointer-events-none" />

      {/* Main Brand Stage Container */}
      <div className="relative z-10 flex flex-col items-center max-w-md px-6 text-center">
        
        {/* Official Logo Display Card */}
        <div className="relative mb-5 group">
          {/* Subtle Ambient Pulse Ring */}
          <div className="absolute -inset-2 bg-gradient-to-r from-[#0077B6] via-[#759783] to-[#FF7F50] rounded-3xl blur-md opacity-60 group-hover:opacity-100 transition duration-700 animate-pulse" />
          
          {/* Official Logo White Emblem Card */}
          <div className="relative bg-white/98 backdrop-blur-md rounded-2xl px-6 py-4 sm:px-8 sm:py-5 shadow-2xl border border-white/40 flex flex-col items-center justify-center transform transition-transform duration-500 hover:scale-105">
            {/* Wordmark: Rent (Bold Charcoal) + Low (Light Sage Green) */}
            <div className="flex items-baseline select-none">
              <span className="text-4xl sm:text-5xl font-black tracking-tight text-[#181A1E] font-sans drop-shadow-xs">
                Rent
              </span>
              <span className="text-4xl sm:text-5xl font-light tracking-wide text-[#759783] font-sans ml-0.5">
                Low
              </span>
            </div>

            {/* Official Hindi Tagline directly positioned under 'Low' */}
            <div className="w-full flex justify-end -mt-1 sm:-mt-1.5 pr-1 sm:pr-2">
              <span className="text-base sm:text-lg font-extrabold text-[#181A1E] font-sans tracking-wide leading-none">
                रेंट से लो
              </span>
            </div>
          </div>
        </div>

        {/* Official Website URL Badge: RentLow.co.in */}
        <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-slate-800/90 border border-slate-700/90 shadow-md">
          <Globe className="w-3.5 h-3.5 text-[#38BDF8]" />
          <span className="text-xs sm:text-sm font-bold tracking-wide font-mono text-white">
            RentLow<span className="text-[#38BDF8]">.co.in</span>
          </span>
        </div>

        {/* Servicing Cities Badge */}
        <div className="mt-3 flex items-center gap-1.5 text-slate-300 text-xs font-mono font-medium">
          <MapPin className="w-3.5 h-3.5 text-[#38BDF8] animate-pulse" />
          <span>Bhopal &amp; Sehore&apos;s Trusted Appliance Rental</span>
        </div>

        {/* Animated Loading Bar */}
        <div className="w-52 sm:w-64 mt-6">
          <div className="h-1.5 w-full bg-slate-800/90 rounded-full overflow-hidden p-0.5 border border-slate-700/60 shadow-inner">
            <div
              className={`h-full bg-gradient-to-r from-[#0077B6] via-[#759783] to-[#FF7F50] rounded-full transition-all duration-300 ease-out shadow-sm ${
                loadingStep === 0
                  ? "w-1/4"
                  : loadingStep === 1
                  ? "w-2/4"
                  : loadingStep === 2
                  ? "w-4/5"
                  : "w-full"
              }`}
            />
          </div>
          
          {/* Dynamic Status Text */}
          <p className="mt-2.5 text-[11px] font-mono text-slate-400 h-4 flex items-center justify-center transition-all duration-200">
            {loadingMessages[loadingStep]}
          </p>
        </div>

        {/* Trust Value Badges */}
        <div className="mt-7 flex items-center justify-center gap-2.5 text-[10px] font-semibold text-slate-300 font-mono">
          <span className="flex items-center gap-1 bg-slate-800/70 border border-slate-700/70 px-2 py-1 rounded-md">
            <Zap className="w-3 h-3 text-amber-400" /> ₹0 Deposit
          </span>
          <span className="flex items-center gap-1 bg-slate-800/70 border border-slate-700/70 px-2 py-1 rounded-md">
            <ShieldCheck className="w-3 h-3 text-emerald-400" /> Free Delivery
          </span>
          <span className="flex items-center gap-1 bg-slate-800/70 border border-slate-700/70 px-2 py-1 rounded-md">
            <Wrench className="w-3 h-3 text-sky-400" /> Free Service
          </span>
        </div>
      </div>
    </div>
  );
}
